// src/hooks/useGeneratePanelData.ts
import { useMutation } from "@tanstack/react-query";

import { generatePanelData, GeneratePanelDataResponse, PanelGenerationError } from "../../api/geneCodes/AiPanelDataApi";
import { useNotifications } from "../../context/NotificationContext";

export interface GeneratePanelDataVariables {
	sampleId: string;
	/** Overwrites panels that already have a report; labiq requires labiq.genecodes.regenerate. */
	force?: boolean;
}

export const useGeneratePanelData = () => {
	const { showSuccess, showError, showWarning } = useNotifications();

	return useMutation<GeneratePanelDataResponse, unknown, GeneratePanelDataVariables>({
		mutationFn: ({ sampleId, force }) => generatePanelData(sampleId, force),
		onSuccess: (data) => {
			// Queued, not written — each panel notifies separately as the job finishes it.
			showSuccess(
				"AI panel analysis queued",
				`${data.panels_count} panel${data.panels_count === 1 ? "" : "s"} queued for sample ${data.sample_id}. You will be notified as each one completes.`
			);
		},
		onError: (error) => {
			const status = error instanceof PanelGenerationError ? error.status : undefined;
			const detail = error instanceof Error ? error.message : undefined;

			// 503 is the auth server being unreachable, not a decision about this request.
			if (status === 503) {
				showWarning("Try again shortly", detail);
				return;
			}

			showError(status === 403 ? "Not permitted" : "Could not start the AI panel analysis", detail);
		},
	});
};
